import React, { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTheme } from '../contexts/ThemeContext'
import { useAuth } from '../contexts/AuthContext'
import ColorPicker from './ColorPicker'
import { 
  LayoutDashboard, 
  Users, 
  FileText, 
  Menu, 
  X, 
  Sun, 
  Moon, 
  Monitor,
  Palette,
  LogOut,
  User,
  ChevronDown,
  ChevronRight
} from 'lucide-react'

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [colorPickerOpen, setColorPickerOpen] = useState(false)
  const { theme, toggleTheme, isDark } = useTheme()
  const { user, logout } = useAuth()
  const { permissions } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  const topItems = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  ]

  const navigationGroups = [
    {
      title: 'Cadastro',
      items: [
        { name: 'PESSOAS', href: '/pessoas', icon: Users, perm: 'ver_pessoa' },
        { name: 'FUNCIONARIOS', href: '/funcionarios', icon: Users, perm: 'ver_funcionario' },
      ]
    },
    {
      title: 'Gabinete',
      items: [
        { name: 'ATENDIMENTOS', href: '/atendimentos', icon: FileText, perm: 'ver_atendimento' },
      ]
    }
  ]

  const [openCadastro, setOpenCadastro] = useState(true)
  const [openGabinete, setOpenGabinete] = useState(true)

  const isActive = (href) => location.pathname === href
  const isGroupActive = (group) => group.items.some(i => isActive(i.href))

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  const getThemeIcon = () => {
    switch (theme) {
      case 'light':
        return <Sun className="h-5 w-5" />
      case 'dark':
        return <Moon className="h-5 w-5" />
      case 'auto':
        return <Monitor className="h-5 w-5" />
      case 'custom':
        return <Palette className="h-5 w-5" />
      default:
        return <Sun className="h-5 w-5" />
    }
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'hsl(var(--background))' }}>
      {/* Sidebar para desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-64 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto px-6 pb-4" style={{ backgroundColor: 'hsl(var(--sidebar))', borderRight: '1px solid hsl(var(--border))' }}>
          {/* Logo */}
          <div className="flex h-16 shrink-0 items-center">
            <h1 className="text-2xl font-bold" style={{ color: 'hsl(var(--foreground))' }}>
              Gabinett
            </h1>
          </div>

          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul role="list" className="-mx-2 space-y-1 mb-4">
                  {topItems.map((item) => (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        className={`
                          group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold transition-colors
                          ${isActive(item.href)
                            ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400'
                            : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:text-primary-400 dark:hover:bg-gray-700'
                          }
                        `}
                      >
                        <item.icon className="h-6 w-6 shrink-0" />
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>

                <div className="-mx-2 space-y-1">
                  <button
                    onClick={() => setOpenCadastro((v) => !v)}
                    className={`group flex w-full items-center gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold transition-colors ${
                      isGroupActive(navigationGroups[0])
                        ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400'
                        : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:text-primary-400 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Users className="h-6 w-6 shrink-0" />
                    Cadastro
                    {openCadastro ? <ChevronDown className="ml-auto h-4 w-4" /> : <ChevronRight className="ml-auto h-4 w-4" />}
                  </button>
                  {openCadastro && (
                    <ul role="list" className="mt-1 -mx-2 space-y-1">
                      {navigationGroups[0].items.filter(i => (permissions?.[i.perm] || 'nao') === 'sim').map((item) => (
                        <li key={item.name}>
                          <Link
                            to={item.href}
                            className={`group ml-7 flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold transition-colors ${
                              isActive(item.href)
                                ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400'
                                : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:text-primary-400 dark:hover:bg-gray-700'
                            }`}
                          >
                            <item.icon className="h-6 w-6 shrink-0" />
                            {item.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}

                  <button
                    onClick={() => setOpenGabinete((v) => !v)}
                    className={`group flex w-full items-center gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold transition-colors ${
                      isGroupActive(navigationGroups[1])
                        ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400'
                        : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:text-primary-400 dark:hover:bg-gray-700'
                    }`}
                  >
                    <FileText className="h-6 w-6 shrink-0" />
                    Gabinete
                    {openGabinete ? <ChevronDown className="ml-auto h-4 w-4" /> : <ChevronRight className="ml-auto h-4 w-4" />}
                  </button>
                  {openGabinete && (
                    <ul role="list" className="mt-1 -mx-2 space-y-1">
                      {navigationGroups[1].items.filter(i => (permissions?.[i.perm] || 'nao') === 'sim').map((item) => (
                        <li key={item.name}>
                          <Link
                            to={item.href}
                            className={`group ml-7 flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold transition-colors ${
                              isActive(item.href)
                                ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400'
                                : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:text-primary-400 dark:hover:bg-gray-700'
                            }`}
                          >
                            <item.icon className="h-6 w-6 shrink-0" />
                            {item.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Sidebar mobile */}
      <div className={`lg:hidden ${sidebarOpen ? 'fixed inset-0 z-50' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-900/80" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 z-50 w-64" style={{ backgroundColor: 'hsl(var(--sidebar))' }}>
          <div className="flex h-16 shrink-0 items-center justify-between px-6" style={{ borderBottom: '1px solid hsl(var(--border))' }}>
            <h1 className="text-2xl font-bold" style={{ color: 'hsl(var(--foreground))' }}>
                Gabinett
              </h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex flex-1 flex-col px-6 py-4">
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul role="list" className="-mx-2 space-y-1 mb-4">
                  {topItems.map((item) => (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        onClick={() => setSidebarOpen(false)}
                        className={`
                          group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold transition-colors
                          ${isActive(item.href)
                            ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400'
                            : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:text-primary-400 dark:hover:bg-gray-700'
                          }
                        `}
                      >
                        <item.icon className="h-6 w-6 shrink-0" />
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
                <div className="-mx-2 space-y-1">
                  <button
                    onClick={() => setOpenCadastro((v) => !v)}
                    className={`group flex w-full items-center gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold transition-colors ${
                      isGroupActive(navigationGroups[0])
                        ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400'
                        : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:text-primary-400 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Users className="h-6 w-6 shrink-0" />
                    Cadastro
                    {openCadastro ? <ChevronDown className="ml-auto h-4 w-4" /> : <ChevronRight className="ml-auto h-4 w-4" />}
                  </button>
                  {openCadastro && (
                    <ul role="list" className="mt-1 -mx-2 space-y-1">
                      {navigationGroups[0].items.map((item) => (
                        <li key={item.name}>
                          <Link
                            to={item.href}
                            onClick={() => setSidebarOpen(false)}
                            className={`group ml-7 flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold transition-colors ${
                              isActive(item.href)
                                ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400'
                                : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:text-primary-400 dark:hover:bg-gray-700'
                            }`}
                          >
                            <item.icon className="h-6 w-6 shrink-0" />
                            {item.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                  <button
                    onClick={() => setOpenGabinete((v) => !v)}
                    className={`group flex w-full items-center gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold transition-colors ${
                      isGroupActive(navigationGroups[1])
                        ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400'
                        : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:text-primary-400 dark:hover:bg-gray-700'
                    }`}
                  >
                    <FileText className="h-6 w-6 shrink-0" />
                    Gabinete
                    {openGabinete ? <ChevronDown className="ml-auto h-4 w-4" /> : <ChevronRight className="ml-auto h-4 w-4" />}
                  </button>
                  {openGabinete && (
                    <ul role="list" className="mt-1 -mx-2 space-y-1">
                      {navigationGroups[1].items.map((item) => (
                        <li key={item.name}>
                          <Link
                            to={item.href}
                            onClick={() => setSidebarOpen(false)}
                            className={`group ml-7 flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold transition-colors ${
                              isActive(item.href)
                                ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400'
                                : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:text-primary-400 dark:hover:bg-gray-700'
                            }`}
                          >
                            <item.icon className="h-6 w-6 shrink-0" />
                            {item.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Conteúdo principal */}
      <div className="lg:pl-64">
        {/* Topbar */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8" style={{ backgroundColor: 'hsl(var(--header))', borderBottom: '1px solid hsl(var(--border))' }}>
          {/* Botão menu mobile */}
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 dark:text-gray-300 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1" />
            
            {/* Controles da direita */}
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              {/* Botão de tema */}
              <button
                onClick={toggleTheme}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                title={`Tema atual: ${theme}`}
              >
                {getThemeIcon()}
              </button>

              {/* Botão de customização de cores */}
              <button
                onClick={() => setColorPickerOpen(true)}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                title="Personalizar cores"
              >
                <Palette className="h-5 w-5" />
              </button>

              {/* Usuário */}
              <div className="flex items-center gap-x-2">
                <div className="hidden sm:flex sm:flex-col sm:items-end">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {user?.name || 'Usuário'}
                  </p>
                </div>
                <div className="h-8 w-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                  <User className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                </div>
              </div>

              {/* Botão logout */}
              <button
                onClick={handleLogout}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                title="Sair"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Conteúdo da página */}
        <main className="py-6">
          <div className="px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>

      {/* Modal de customização de cores */}
      <ColorPicker 
        isOpen={colorPickerOpen} 
        onClose={() => setColorPickerOpen(false)} 
      />
    </div>
  )
}

export default Layout
